import React, { useEffect, useState } from "react";
import { X, FileText, Settings } from "lucide-react";
import classes from "./MaterialIssuedModal.module.css";
import axios from "axios";
import Swal from "sweetalert2";
import { BASE_URL } from "../../Components/API/Api";

const MaterialIssuedModal = ({ isOpen, onClose }) => {
  const [materials, setMaterials] = useState([]);
  const [productionOrders, setProductionOrders] = useState([]);
  const [selectedProductionOrder, setSelectedProductionOrder] = useState("");
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [loadingMaterials, setLoadingMaterials] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  /* =========================
     FETCH PRODUCTION ORDERS
  ========================== */
  useEffect(() => {
    if (isOpen) {
      fetchProductionOrders();
      setMaterials([]);
      setSelectedProductionOrder("");
    }
  }, [isOpen]);

  const fetchProductionOrders = async () => {
    try {
      setLoadingOrders(true);
      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        `${BASE_URL}/v1/production_order/planned`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (Array.isArray(res.data?.data)) {
        setProductionOrders(res.data.data);
      }
    } catch (error) {
      console.error("Error fetching production orders:", error);
    } finally {
      setLoadingOrders(false);
    }
  };

  /* =========================
     FETCH MATERIALS BY PRODUCTION ORDER ID
  ========================== */
  const fetchProductionMaterials = async (productionOrderId) => {
    try {
      setLoadingMaterials(true);
      const token = localStorage.getItem("authToken");

      const res = await axios.get(
        `${BASE_URL}/v1/production_order/materials`,
        {
          params: {
            production_order_id: productionOrderId,
          },
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (Array.isArray(res.data?.data)) {
        setMaterials(
          res.data.data.map((item) => ({
            // Check your API response structure - you might need to change these
            rowId: item.id, // This is the requisition ID
            materialId: Number(item.material_id) || Number(item.id), // FIX THIS LINE
            // If your API has a separate material_id field, use it
            // materialId: item.material_id ? Number(item.material_id) : Number(item.id),
            material: item.material || "",
            requisitionNo: item.requisition_number || "",
            quantityRequested: Number(item.quantity_required) || 0,
            quantityIssued: item.quantity_issued || "",
            uom: item.unit_of_measurement || "",
            unitCost: item.unit_cost || "",
            totalValue: item.total_cost || "",
          }))
        );
      } else {
        setMaterials([]);
      }
    } catch (error) {
      console.error("Error fetching production materials:", error);
      setMaterials([]);
    } finally {
      setLoadingMaterials(false);
    }
  };
  /* =========================
     SUBMIT MATERIAL ISSUED
  ========================== */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedProductionOrder) {
      Swal.fire("Warning", "Please select a production order", "warning");
      return;
    }

    // Ensure all quantityIssued are numbers > 0
    for (let m of materials) {
      if (!m.quantityIssued || Number(m.quantityIssued) <= 0) {
        Swal.fire("Warning", "Please enter quantity issued for all items", "warning");
        return;
      }
    }

    const token = localStorage.getItem("authToken");
    const payload = {
      production_order_id: Number(selectedProductionOrder),
      requisition_id: materials.map(m => m.rowId),
      material_id: materials.map(m => m.materialId), // now correctly sends [2,3]
      quantity_issued: materials.map(m => Number(m.quantityIssued)),
      uom: materials.map(m => m.uom),
      unit_cost: materials.map(m => Number(m.unitCost)),
      total_cost: materials.map(m =>
        Number(m.quantityIssued) * Number(m.unitCost)
      ),
    };





    console.log("FINAL PAYLOAD", payload);




    try {
      setSubmitting(true);

      const res = await axios.post(
        `${BASE_URL}/v1/material_requisition/issuance`,
        payload,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data?.status) {
        Swal.fire("Success", "Materials issued successfully", "success").then(() => {
          onClose();
          setMaterials([]);
          setSelectedProductionOrder("");
        });
      } else {
        Swal.fire("Error", JSON.stringify(res.data?.message), "error");
      }
    } catch (error) {
      const backendMessage = error.response?.data?.message || "Submission failed";
      Swal.fire("Error", JSON.stringify(backendMessage), "error");
    } finally {
      setSubmitting(false);
    }
  };


  const totalTransferValue = materials.reduce(
    (sum, item) => sum + Number(item.totalValue || 0),
    0
  );


  if (!isOpen) return null;

  return (
    <div className={classes.overlay}>
      <div className={classes.modal}>
        {/* Header */}
        <div className={classes.header}>
          <div className={classes.title}>
            <FileText className={classes.icon} />
            <h2>Materials Issued</h2>
          </div>
          <X className={classes.closeIcon} onClick={onClose} />
        </div>

        <p className={classes.subtitle}>
          Record of materials issued to goods warehouse.
        </p>

        <form className={classes.form} onSubmit={handleSubmit}>
          <div className={classes.row}>
            <div className={classes.field}>
              <label>Production Order No</label>
              <select
                value={selectedProductionOrder}
                onChange={(e) => {
                  const id = e.target.value;
                  setSelectedProductionOrder(id);

                  if (id) {
                    fetchProductionMaterials(id); // ✅ NOW SENDS production_order_id
                  } else {
                    setMaterials([]);
                  }
                }}
              >
                <option value="">
                  {loadingOrders
                    ? "Loading production orders..."
                    : "-- Select Production Order --"}
                </option>
                {productionOrders.map((po) => (
                  <option key={po.id} value={po.id}>
                    {po.production_number}
                  </option>
                ))}
              </select>
            </div>
          </div>

          {/* Materials Section */}
          <div className={classes.sectionHeader}>
            <Settings className={classes.sectionIcon} />
            <h3>Material Issued Details</h3>
          </div>

          <table className={classes.table}>
            <thead>
              <tr>
                <th>Materials</th>
                <th>Requisition No</th>
                <th>Qty Requested</th>
                <th>Qty Issued</th>
                <th>UOM</th>
                <th>Unit Cost</th>
                <th>Total Value</th>
              </tr>
            </thead>
            <tbody>
              {loadingMaterials ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    Loading materials...
                  </td>
                </tr>
              ) : materials.length === 0 ? (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>
                    No materials found
                  </td>
                </tr>
              ) : (
                materials.map((row, index) => (
                  <tr key={index}>
                    <td><input value={row.material} readOnly /></td>
                    <td><input value={row.requisitionNo} readOnly /></td>
                    <td><input value={row.quantityRequested} readOnly /></td>
                    <td>
                      <input
                        type="number"
                        value={row.quantityIssued}
                        onChange={(e) => {
                          const updated = [...materials];
                          const qtyIssued = e.target.value;

                          updated[index].quantityIssued = qtyIssued;

                          if (qtyIssued && updated[index].unitCost) {
                            updated[index].totalValue =
                              Number(qtyIssued) * Number(updated[index].unitCost);
                          } else {
                            updated[index].totalValue = "";
                          }

                          setMaterials(updated);
                        }}
                      />


                    </td>
                    <td><input value={row.uom} readOnly /></td>
                    <td className={classes.rightAlign}>
                      <input
                        type="number"
                        placeholder="Unit cost"
                        value={row.unitCost}
                        onChange={(e) => {
                          const updated = [...materials];
                          const unitCost = e.target.value;

                          updated[index].unitCost = unitCost;

                          if (unitCost && updated[index].quantityIssued) {
                            updated[index].totalValue =
                              Number(unitCost) * Number(updated[index].quantityIssued);
                          } else {
                            updated[index].totalValue = "";
                          }

                          setMaterials(updated);
                        }}
                      />
                    </td>

                    <td className={classes.rightAlign}>
                      <input
                        type="number"
                        value={row.totalValue}
                        readOnly
                      />
                    </td>

                  </tr>
                ))
              )}
            </tbody>
          </table>

          <div className={`${classes.row} ${classes.totalRow}`}>
            <div className={classes.totalField}>
              <label>Total Transfer Value:</label>
              <div className={classes.totalValue}>
                {totalTransferValue.toLocaleString()}
              </div>
            </div>
          </div>

          <div className={classes.submitSection}>
            <button
              type="submit"
              className={classes.submitBtn}
              disabled={submitting}
            >
              {submitting ? "Submitting..." : "Complete Transfer"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default MaterialIssuedModal;
